import React, { useEffect, useState, useRef } from "react";
import { useAccount } from "wagmi";
import BuyNumberPanel from "./BuyNumberPanel";
import {
  Box,
  Heading,
  Progress,
  Text,
  Flex,
  useToast,
  useColorMode,
  Tooltip,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { apiFetch } from "../utils/api";

const POLL_INTERVAL = 10000;
const DRAW_CYCLE = 180000;

const MotionHeading = motion(Heading);

const LotteryNumberDisplay = () => {
  const [lotteryNumber, setLotteryNumber] = useState("------");
  const [generatedAt, setGeneratedAt] = useState(null);
  const [progress, setProgress] = useState(0);
  const [tierPrizes, setTierPrizes] = useState({
    1: null,
    2: null,
    3: null,
    4: null,
  });

  const [calcMessage, setCalcMessage] = useState("");
  const [showHacker, setShowHacker] = useState(false);
  const [showFinalPrizes, setShowFinalPrizes] = useState(false);
  const [hackerText, setHackerText] = useState("");
  const timeoutsRef = useRef([]);
  const latestNumberRef = useRef(null);

  const toast = useToast();
  const { isConnected } = useAccount();
  const { colorMode } = useColorMode();

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  /*
  useEffect(() => {
    if (!showHacker) return;

    const charset = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ#$%&";

    const makeLine = () => {
      let s = "";

      for (let i = 0; i < 32; i++) {
        s += charset[Math.floor(Math.random() * charset.length)];
      }

      return s;
    };

    setHackerText(makeLine());

    const interval = setInterval(() => {
      setHackerText(makeLine());
    }, 80);

    return () => clearInterval(interval);
  }, [showHacker]);
  */

  const formatTierAmount = (value) =>
    typeof value === "number" && !Number.isNaN(value)
      ? value.toFixed(2)
      : "0.00";

  const startTierAnimation = (newPrizes) => {
    setTierPrizes(newPrizes);
    setShowFinalPrizes(false);
    setShowHacker(false);
    setCalcMessage("");

    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    const steps = [];

    let elapsed = 0;

    steps.forEach((step) => {
      const timeoutId = setTimeout(() => {
        setCalcMessage(step.text);
      }, elapsed);

      timeoutsRef.current.push(timeoutId);
      elapsed += step.duration;
    });

    const hackerEnd = elapsed + 500;

    const finalId = setTimeout(() => {
      setCalcMessage("");
      setShowFinalPrizes(true);
    }, hackerEnd + 500);

    timeoutsRef.current.push(finalId);
  };

  const fetchTierPrizes = async () => {
    try {
      const json = await apiFetch("/tiers");

      if (json?.per_tier) {
        const newPrizes = {
          1: json.per_tier[1] ?? 0,
          2: json.per_tier[2] ?? 0,
          3: json.per_tier[3] ?? 0,
          4: json.per_tier[4] ?? 0,
        };

        startTierAnimation(newPrizes);
      }
    } catch (error) {
      console.error("Failed to fetch tier prizes:", error);
    }
  };

  const fetchLatestNumber = async () => {
    try {
      const data = await apiFetch("/app/state");
      const currentNumber = data?.currentNumber;

      if (!currentNumber?.number || !currentNumber?.generatedAt) {
        return;
      }

      const previousNumber = latestNumberRef.current;

      if (currentNumber.number !== previousNumber) {
        latestNumberRef.current = currentNumber.number;

        setLotteryNumber(currentNumber.number);
        setGeneratedAt(new Date(currentNumber.generatedAt).getTime());
        setProgress(0);

        if (previousNumber) {
          toast({
            title: "🎉 New Lottery Number!",
            description: `A new number ${currentNumber.number} has been drawn.`,
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch latest number:", error);
    }
  };

  useEffect(() => {
    fetchLatestNumber();

    const interval = setInterval(fetchLatestNumber, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchTierPrizes();

    const interval = setInterval(fetchTierPrizes, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateProgress = () => {
      if (!generatedAt) {
        return;
      }

      const now = Date.now();
      const elapsed = now - generatedAt;
      const percent = Math.min((elapsed / DRAW_CYCLE) * 100, 100);

      setProgress(percent);
    };

    updateProgress();

    const progressTimer = setInterval(updateProgress, 1000);

    return () => clearInterval(progressTimer);
  }, [generatedAt]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(lotteryNumber);

      toast({
        title: "Copied!",
        description: `Lottery number ${lotteryNumber} copied to clipboard.`,
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Could not copy the number.",
        status: "error",
        duration: 2500,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <Box width="90%" maxW="800px" mx="auto" mt={8}>
      <Flex
        position="relative"
        zIndex={10}
        minHeight="40vh"
        direction="column"
        justify="center"
        align="center"
        textAlign="center"
        mb={2}
        pt={4}
      >
        <AnimatePresence mode="wait">
          <MotionHeading
            key={lotteryNumber}
            fontSize={{ base: "7xl", md: "8xl" }}
            letterSpacing="wider"
            mb={4}
            cursor="pointer"
            onClick={handleCopy}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.4 }}
            display="flex"
            gap={3}
          >
            <Tooltip
              label="Up to 10 Points + 5000 USDT Bonus!"
              hasArrow
              placement="top"
              bg="white"
              color="gray.700"
              fontSize="sm"
              px={3}
              py={1}
              borderRadius="md"
              boxShadow="lg"
              fontWeight="medium"
            >
              <Box as="span">{lotteryNumber || "------"}</Box>
            </Tooltip>
          </MotionHeading>
        </AnimatePresence>

        <Tooltip
          label="A new number every 3 minutes.."
          hasArrow
          placement="bottom"
          bg="white"
          color="gray.700"
          fontSize="sm"
          px={4}
          py={2}
          borderRadius="md"
          boxShadow="lg"
          textAlign="center"
          fontWeight="medium"
        >
          <Progress
            value={progress}
            size="lg"
            colorScheme="teal"
            width={{ base: "90%", md: "600px" }}
            height="14px"
            borderRadius="full"
            mt={{ base: 0, md: 6 }}
            trackColor={colorMode === "light" ? "gray.400" : "gray.700"}
          />
        </Tooltip>

        {calcMessage && (
          <Text
            mt={3}
            fontSize="sm"
            color={colorMode === "light" ? "gray.700" : "gray.300"}
            fontFamily="mono"
          >
            {calcMessage}
          </Text>
        )}

        {!calcMessage && showHacker && (
          <Text
            mt={3}
            fontSize="sm"
            color={colorMode === "light" ? "gray.700" : "gray.300"}
            fontFamily="mono"
          >
            {hackerText}
          </Text>
        )}

        {/*
        {!calcMessage && !showHacker && showFinalPrizes && (
          <Tooltip
            label="Estimated tier rewards based on current prize pool. Final payouts are calculated at 21:00 UTC"
            hasArrow
            placement="bottom"
            bg="white"
            color="gray.700"
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="md"
            boxShadow="lg"
            fontWeight="medium"
            textAlign="center"
          >
            <Text
              mt={3}
              fontSize="sm"
              color={colorMode === "light" ? "gray.700" : "gray.300"}
              fontFamily="mono"
            >
              {`Tier1: $${formatTierAmount(
                tierPrizes[1]
              )}  Tier2: $${formatTierAmount(
                tierPrizes[2]
              )}  Tier3: $${formatTierAmount(
                tierPrizes[3]
              )}  Tier4: $${formatTierAmount(tierPrizes[4])}`}
            </Text>
          </Tooltip>
        )}
        */}
      </Flex>

      <Box pb={{ base: 20, md: 10 }}>
        {isConnected ? (
          <BuyNumberPanel />
        ) : (
          <Text fontSize="lg" mt={6} color="gray.400" textAlign="center">
            Connect your wallet to participate
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default LotteryNumberDisplay;
